import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Descriptions,
  Table,
  Tag,
  Space,
  Button,
  Select,
  Input,
  Alert,
  Typography,
  Divider,
  Popconfirm,
  Row,
  Col,
  Card,
  Statistic,
  InputNumber,
  Form,
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { hasPermission } from '../../utils/permissions';
import {
  fetchQuotationRequest,
  updateQuotationStatusRequest,
  convertQuotationRequest,
} from '../../redux/actions/quotationActions';

const { Text, Title } = Typography;
const { Option } = Select;

const fmtMoney = (v) => (v != null ? `₹ ${Number(v).toFixed(2)}` : '—');
const fmtDate  = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');

export const getQuotationStatusTag = (status) => {
  const map = {
    received:  { color: 'blue',    text: 'Received'  },
    expired:   { color: 'orange',  text: 'Expired'   },
    cancelled: { color: 'red',     text: 'Cancelled' },
    converted: { color: 'success', text: 'Converted' },
  };
  const cfg = map[status] || { color: 'default', text: status || '—' };
  return <Tag color={cfg.color} style={{ fontWeight: 500 }}>{cfg.text}</Tag>;
};

/**
 * QuotationDetail
 *
 * Props:
 *  open        — boolean
 *  onClose     — callback
 *  quotationId — uint
 */
const QuotationDetail = ({ open, onClose, quotationId }) => {
  const dispatch = useDispatch();
  const permissions = useSelector((s) => s.auth.permissions || []);
  const { currentQuotation, actionLoading, loading } = useSelector((s) => s.quotations);

  const canUpdate  = hasPermission(permissions, 'purchase_order/update');
  const canConvert = hasPermission(permissions, 'purchase_order/create');

  const [convertPoId, setConvertPoId] = useState('');
  const [convertError, setConvertError] = useState('');

  useEffect(() => {
    if (open && quotationId) {
      dispatch(fetchQuotationRequest(quotationId));
    }
  }, [open, quotationId, dispatch]);

  const q = currentQuotation?.id === quotationId ? currentQuotation : null;

  const handleStatusChange = (status) => {
    dispatch(updateQuotationStatusRequest(quotationId, status));
  };

  const handleConvert = () => {
    const poId = parseInt(convertPoId, 10);
    if (!poId || poId <= 0) {
      setConvertError('Please enter a valid PO ID');
      return;
    }
    dispatch(convertQuotationRequest(quotationId, poId));
    setConvertPoId('');
    setConvertError('');
  };

  const itemColumns = [
    { title: '#', key: 'idx', width: 36, render: (_, __, i) => <Text type="secondary" style={{ fontSize: 11 }}>{i + 1}</Text> },
    { title: 'Product', dataIndex: 'product_name', key: 'product_name', render: (v, r) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{v}</Text>
          {r.unit && <Tag style={{ marginLeft: 4, fontSize: 10 }} color="geekblue">{r.unit}</Tag>}
          {r.hsn_sac_code && <div style={{ fontSize: 11, color: '#8c8c8c' }}>HSN: {r.hsn_sac_code}</div>}
        </div>
      ),
    },
    { title: 'Qty', dataIndex: 'quantity', key: 'qty', width: 80, align: 'right',
      render: (v, r) => `${Number(v).toFixed(2)} ${r.unit || ''}` },
    { title: 'Rate', dataIndex: 'quoted_rate', key: 'rate', width: 100, align: 'right',
      render: (v) => fmtMoney(v) },
    { title: 'Disc %', dataIndex: 'discount', key: 'disc', width: 70, align: 'center',
      render: (v) => v > 0 ? <Tag color="green">{v}%</Tag> : '—' },
    { title: 'GST %', dataIndex: 'tax_slab', key: 'tax', width: 70, align: 'center',
      render: (v) => v ? <Tag color="orange">{v}%</Tag> : '0%' },
    { title: 'Taxable', dataIndex: 'taxable_amount', key: 'taxable', width: 100, align: 'right',
      render: (v) => <Text style={{ fontSize: 12 }}>{fmtMoney(v)}</Text> },
    { title: 'Tax ₹', dataIndex: 'tax_amount', key: 'tax_amt', width: 90, align: 'right',
      render: (v) => <Text style={{ fontSize: 12, color: '#fa8c16' }}>{fmtMoney(v)}</Text> },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 110, align: 'right',
      render: (v) => <Text strong style={{ color: '#1890ff', fontSize: 13 }}>{fmtMoney(v)}</Text> },
  ];

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined style={{ color: '#1890ff' }} />
          Quotation {q?.quotation_number || `#${quotationId}`}
          {q && getQuotationStatusTag(q.status)}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={900}
      destroyOnClose
      styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
    >
      {loading && !q ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : !q ? null : (
        <>
          {/* ── Header info ─────────────────────────────────────────── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ background: '#f0f5ff', borderColor: '#adc6ff' }}>
                <Statistic title="Total Amount" value={Number(q.total_amount).toFixed(2)} prefix="₹"
                  valueStyle={{ fontSize: 15, color: '#1890ff' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
                <Statistic title="Total Tax" value={Number(q.total_tax).toFixed(2)} prefix="₹"
                  valueStyle={{ fontSize: 15, color: '#fa8c16' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                <Statistic title="Total Discount" value={Number(q.total_discount).toFixed(2)} prefix="₹"
                  valueStyle={{ fontSize: 15, color: '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Items</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{q.items?.length || 0}</div>
              </Card>
            </Col>
          </Row>

          <Descriptions size="small" bordered column={{ xs: 1, sm: 2 }} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Quotation #"><Text strong>{q.quotation_number}</Text></Descriptions.Item>
            <Descriptions.Item label="Vendor"><Text strong>{q.vendor_name}</Text></Descriptions.Item>
            <Descriptions.Item label="Quotation Date">{fmtDate(q.quotation_date)}</Descriptions.Item>
            <Descriptions.Item label="Valid Until">
              {q.valid_until ? (
                <span>
                  {fmtDate(q.valid_until)}
                  {dayjs(q.valid_until).isBefore(dayjs()) && (
                    <Tag color="red" style={{ marginLeft: 6 }}>Expired</Tag>
                  )}
                </span>
              ) : '—'}
            </Descriptions.Item>
            {q.converted_to_po_id && (
              <Descriptions.Item label="Converted to PO">
                <Text strong style={{ color: '#52c41a' }}>PO #{q.converted_to_po_id}</Text>
                {q.converted_at && (
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                    on {fmtDate(q.converted_at)}
                  </Text>
                )}
              </Descriptions.Item>
            )}
            {q.remarks && (
              <Descriptions.Item label="Remarks" span={2}>{q.remarks}</Descriptions.Item>
            )}
            <Descriptions.Item label="Created By">{q.created_by_name || '—'}</Descriptions.Item>
          </Descriptions>

          {/* ── Items table ──────────────────────────────────────────── */}
          <Divider orientation="left" style={{ fontSize: 13 }}>
            Line Items ({q.items?.length || 0})
          </Divider>
          <Table
            columns={itemColumns}
            dataSource={q.items || []}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 740 }}
          />

          {/* ── Actions ──────────────────────────────────────────────── */}
          {q.status !== 'converted' && (canUpdate || canConvert) && (
            <>
              <Divider style={{ margin: '16px 0 12px' }} />

              {/* Status update */}
              {canUpdate && q.status !== 'converted' && (
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: 13, marginRight: 12 }}>Update Status:</Text>
                  <Space wrap>
                    {q.status !== 'received' && (
                      <Popconfirm title="Mark as received?" onConfirm={() => handleStatusChange('received')} okText="Yes">
                        <Button size="small" icon={<CheckCircleOutlined />} loading={actionLoading}>
                          Mark Received
                        </Button>
                      </Popconfirm>
                    )}
                    {q.status !== 'expired' && (
                      <Popconfirm title="Mark as expired?" onConfirm={() => handleStatusChange('expired')} okText="Yes">
                        <Button size="small" loading={actionLoading}>Mark Expired</Button>
                      </Popconfirm>
                    )}
                    {q.status !== 'cancelled' && (
                      <Popconfirm title="Cancel this quotation?" onConfirm={() => handleStatusChange('cancelled')} okText="Yes" okType="danger">
                        <Button size="small" danger icon={<CloseCircleOutlined />} loading={actionLoading}>
                          Cancel
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                </div>
              )}

              {/* Convert to PO */}
              {canConvert && q.status === 'received' && (
                <div style={{ padding: '12px 16px', background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
                  <Text strong style={{ fontSize: 13 }}>
                    <SwapOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                    Convert to Purchase Order
                  </Text>
                  <div style={{ marginTop: 8, fontSize: 12, color: '#595959', marginBottom: 10 }}>
                    First create the PO using the Purchase Order page with this vendor and these rates,
                    then enter the PO ID below to link it to this quotation.
                  </div>
                  <Space>
                    <InputNumber
                      placeholder="Enter PO ID"
                      style={{ width: 160 }}
                      min={1}
                      precision={0}
                      value={convertPoId || undefined}
                      onChange={(val) => { setConvertPoId(val); setConvertError(''); }}
                      status={convertError ? 'error' : ''}
                    />
                    <Button
                      type="primary"
                      icon={<SwapOutlined />}
                      loading={actionLoading}
                      onClick={handleConvert}
                    >
                      Link &amp; Convert
                    </Button>
                  </Space>
                  {convertError && (
                    <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{convertError}</div>
                  )}
                </div>
              )}
            </>
          )}

          {q.status === 'converted' && (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message={`This quotation was converted to PO #${q.converted_to_po_id} on ${fmtDate(q.converted_at)}.`}
              style={{ marginTop: 12 }}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default QuotationDetail;
